import { formatSnapshotRow, getPortfolioChangesPrompt } from "./blotterUtils";
import { useDispatch, useSelector } from "react-redux";
import { useTickerSearchIndex } from "../../../modules/Search/useTickerSearchIndex";
import { findTickersInText } from "../../../utils/strings";
import { useEffect, useMemo } from "react";
import { fetchTimeseriesReturns } from "../../../api/timeseriesApi";

export const useBlotterSnapshot = ({ data, colorMap, startDate }) => {
  const dispatch = useDispatch();
  const { snapshot } = data || {};
  const endDate = data?.timestamp;

  // Format data for charts
  const snapshotData = Object.entries(snapshot || {})
    .map(([ticker, details], index) => ({
      key: index,
      ticker,
      weight: details.wgt,
      value: details.val,
    }))
    .sort((a, b) => b.weight - a.weight);

  // Only include tickers with weight > 0
  const filteredSnapshotData = snapshotData.filter((d) => d.weight > 0);

  // Prepare data for a donut or pie chart
  const pieData = snapshotData.map((x) => ({
    id: x.ticker === "custodial_cash" ? "CASH" : x.ticker.toUpperCase(),
    value: x.weight * 100,
    color: colorMap?.[x.ticker] || "#ccc",
  }));

  // Ticker handling
  const uniqueTickers = new Set(filteredSnapshotData.map((d) => d.ticker));
  useTickerSearchIndex([...uniqueTickers]);

  // Parsing text for tickers
  const markdownTextBody = data?.text?.content;
  const tickersInText = markdownTextBody && findTickersInText(markdownTextBody, uniqueTickers);

  // Redux store slice for returns
  const percentReturns = useSelector((state) => {
    if (!tickersInText || !startDate || !endDate) return [];

    // Get returns for each ticker
    const returns = tickersInText.map(ticker => {
      const key = `${ticker}_sd:${startDate}_ed:${endDate}`;
      const returnValue = state.timeSeries.percentReturns[key];
      return returnValue ? [ticker, returnValue] : null;
    }).filter(Boolean);

    return returns;
  });

  // Transform returns into array format
  const returnsArray = percentReturns;

  // Identify which tickers we still need to fetch
  const tickersToFetch = useMemo(() => {
    if (!tickersInText || !tickersInText.length) {
      return [];
    }
    return tickersInText.filter((ticker) => {
      const key = `${ticker}_sd:${startDate}_ed:${endDate}`;
      return !percentReturns.some(([t]) => t === ticker);
    });
  }, [tickersInText, startDate, endDate, percentReturns]);

  // Fetch timeseries returns if needed
  useEffect(() => {
    if (tickersToFetch.length > 0 && startDate && endDate) {
      dispatch(fetchTimeseriesReturns({
        symbols: tickersToFetch,
        startDate,
        endDate
      }));
    }
  }, [dispatch, tickersToFetch, startDate, endDate]);

  return {
    snapshotData,
    filteredSnapshotData,
    pieData,
    endDate,
    markdownTextBody,
    returnsArray,
  };
};

const useBlotter = () => {
  const formatSnapshot = (snapshot) => {
    return formatSnapshotRow(snapshot);
  };

  const generatePrompt = (rows, extraHeaders, ...rest) => {
    return getPortfolioChangesPrompt(rows, extraHeaders, ...rest);
  };

  return {
    formatSnapshot,
    generatePrompt,
  };
};

export default useBlotter;